// PWA install plumbing. Chrome only allows triggering the REAL install
// (WebAPK on Android - the kind that lands in the app drawer) from a user
// gesture after it fires `beforeinstallprompt`. We capture that event at
// module load (imported early from main.js) and expose it to the sidebar's
// "Install app" button.

let deferredPrompt = null;
let installed = false;
const listeners = new Set();

function emit() { for (const fn of listeners) { try { fn(); } catch { /* ignore */ } } }

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();          // suppress Chrome's mini-infobar; we own the UX
    deferredPrompt = e;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installed = true;
    emit();
  });
}

export const pwaInstall = {
  /** Already running as an installed app (standalone window)? */
  isStandalone() {
    return window.matchMedia?.("(display-mode: standalone)")?.matches
      || window.navigator.standalone === true;   // iOS Safari
  },

  isIOS() {
    return /iphone|ipad|ipod/i.test(navigator.userAgent)
      || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  },

  /** Chrome has offered installability and we hold the prompt. */
  canPrompt() { return !!deferredPrompt; },

  installedThisSession() { return installed; },

  /**
   * Trigger the native install prompt. Returns "accepted" | "dismissed" |
   * "unavailable" (no captured prompt - e.g. iOS, or criteria not met yet).
   */
  async promptInstall() {
    if (!deferredPrompt) return "unavailable";
    const p = deferredPrompt;
    deferredPrompt = null;       // a prompt event is single-use
    p.prompt();
    const choice = await p.userChoice.catch(() => ({ outcome: "dismissed" }));
    if (choice.outcome !== "accepted") emit(); // allow the button to stay visible
    return choice.outcome === "accepted" ? "accepted" : "dismissed";
  },

  subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
};
